// services/EnterpriseAdminAccess.js
import { getCurrentUserId } from './database';
import proxyapi from './ProxyApi';

/**
 * Restore a revoked user's enterprise access
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} userId - User ID to restore
 * @returns {Promise<Object>} - Response with restoration result
 */
export const restoreEnterpriseUser = async (enterpriseId, userId) => {
  try {
    const response = await proxyapi.post(`/api/v1/enterprise/${enterpriseId}/users/${userId}/restore`);
    return response.data;
  } catch (error) {
    console.error('Error restoring enterprise user:', error);
    throw error;
  }
};

/**
 * Enterprise Admin Access Service
 * 
 * This service provides functions for enterprise admins to access
 * their members' data including projects, views, and versions.
 * All functions require enterprise admin privileges.
 */

/**
 * Get all projects for a specific enterprise member
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} userId - Member user ID
 * @returns {Promise<Object>} - Response with projects array
 */
export const getEnterpriseUserProjects = async (enterpriseId, userId) => {
  try {
    const response = await proxyapi.get(`/api/v1/enterprise/${enterpriseId}/users/${userId}/projects`);
    return response.data;
  } catch (error) {
    console.error('Error fetching enterprise user projects:', error);
    throw error;
  }
};

/**
 * Search projects for a specific enterprise member
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} userId - Member user ID
 * @param {Object} searchParams - Search parameters
 * @param {string} searchParams.q - Search term
 * @param {string} searchParams.sort_by - Field to sort by ('createdAt', 'title')
 * @param {string} searchParams.sort_order - Sort order ('asc', 'desc')
 * @returns {Promise<Object>} - Response with projects array and count
 */
export const searchEnterpriseUserProjects = async (enterpriseId, userId, searchParams = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (searchParams.q) params.append('q', searchParams.q);
    if (searchParams.sort_by) params.append('sort_by', searchParams.sort_by);
    if (searchParams.sort_order) params.append('sort_order', searchParams.sort_order);
    
    const queryString = params.toString();
    const url = `/api/v1/enterprise/${enterpriseId}/users/${userId}/projects/search${queryString ? '?' + queryString : ''}`;
    
    const response = await proxyapi.get(url);
    return response.data;
  } catch (error) {
    console.error('Error searching enterprise user projects:', error);
    throw error;
  }
};

/**
 * Get all views for a project owned by an enterprise member
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} - Response with views array
 */
export const getEnterpriseProjectViews = async (enterpriseId, projectId) => {
  try {
    const response = await proxyapi.get(`/api/v1/enterprise/${enterpriseId}/projects/${projectId}/views`);
    return response.data;
  } catch (error) {
    console.error('Error fetching enterprise project views:', error);
    throw error;
  }
};

/**
 * Get a single view with all its versions (for enterprise admin)
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} viewId - View ID
 * @returns {Promise<Object>} - Complete view object with all versions
 */
export const getEnterpriseView = async (enterpriseId, viewId) => {
  try {
    const response = await proxyapi.get(`/api/v1/enterprise/${enterpriseId}/views/${viewId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching enterprise view:', error);
    throw error;
  }
};

/**
 * Get HTML content for a specific version of a view (for download functionality)
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} viewId - View ID
 * @param {string} versionId - Version ID
 * @returns {Promise<Object>} - Response with HTML content and metadata
 */
export const getEnterpriseViewVersionHtml = async (enterpriseId, viewId, versionId) => {
  try {
    const response = await proxyapi.get(`/api/v1/enterprise/${enterpriseId}/views/${viewId}/versions/${versionId}/html`);
    return response.data;
  } catch (error) {
    console.error('Error fetching enterprise view version HTML:', error);
    throw error;
  }
};

/**
 * Get complete data structure for an enterprise member
 * This returns the full nested structure needed for the enterprise admin interface
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} userId - Member user ID
 * @returns {Promise<Object>} - Complete data structure with projects, views, and versions
 */
export const getEnterpriseUserCompleteData = async (enterpriseId, userId) => {
  try {
    const response = await proxyapi.get(`/api/v1/enterprise/${enterpriseId}/users/${userId}/complete-data`);
    return response.data;
  } catch (error) {
    console.error('Error fetching enterprise user complete data:', error);
    throw error;
  }
};

/**
 * Download HTML for a specific version
 * This function fetches the HTML and triggers a download
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} viewId - View ID
 * @param {string} versionId - Version ID
 * @param {string} customFileName - Optional custom filename (defaults to view title)
 */
export const downloadEnterpriseViewVersion = async (enterpriseId, viewId, versionId, customFileName = null) => {
  try {
    const response = await getEnterpriseViewVersionHtml(enterpriseId, viewId, versionId);
    
    if (response.status === 'success' && response.html) {
      // Create filename
      const viewTitle = response.viewTitle || 'Untitled View';
      const fileName = customFileName || `${viewTitle.replace(/\s+/g, '_')}_v${versionId}.html`;
      
      // Create blob and download
      const blob = new Blob([response.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      return {
        status: 'success',
        message: 'Download started',
        fileName: fileName
      };
    } else {
      throw new Error('No HTML content available for download');
    }
  } catch (error) {
    console.error('Error downloading enterprise view version:', error);
    throw error;
  }
};

/**
 * Display a view version in a new tab/window (for view functionality)
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} viewId - View ID
 * @param {string} versionId - Version ID
 */
export const displayEnterpriseViewVersion = async (enterpriseId, viewId, versionId) => {
  try {
    const response = await getEnterpriseViewVersionHtml(enterpriseId, viewId, versionId);
    
    if (response.status === 'success' && response.html) {
      // Create a blob URL for the HTML content
      const blob = new Blob([response.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open in a new tab
      const newWindow = window.open(url, '_blank');
      
      if (!newWindow) {
        throw new Error('Unable to open new window. Please check your popup blocker settings.');
      }
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
      
      return {
        status: 'success',
        message: 'View opened in new tab'
      };
    } else {
      throw new Error('No HTML content available for viewing');
    }
  } catch (error) {
    console.error('Error displaying enterprise view version:', error);
    throw error;
  }
};

/**
 * Helper function to check if current user is an enterprise admin
 * @param {string} enterpriseId - Enterprise ID to check admin status for
 * @returns {Promise<boolean>} - True if user is admin of the enterprise
 */
export const isEnterpriseAdmin = async (enterpriseId) => {
  try {
    const currentUserId = await getCurrentUserId();
    
    // Try to fetch enterprise details - this will only work if user is admin
    const response = await proxyapi.get(`/api/v1/enterprise/${enterpriseId}?userId=${currentUserId}`);
    
    return response.data.status === 'success';
  } catch (error) {
    // If request fails, user is not admin
    return false;
  }
};

/**
 * Format version data for display in the UI
 * @param {Array} versions - Array of version objects
 * @returns {Array} - Formatted versions sorted by creation time
 */
export const formatVersionsForDisplay = (versions) => {
  if (!versions || !Array.isArray(versions)) {
    return [];
  }
  
  return versions
    .map((version, index) => ({
      ...version,
      displayName: `Version ${versions.length - index}`, // Display in reverse order
      formattedDate: version.time_created ? new Date(version.time_created).toLocaleDateString() : 'Unknown',
      isProcessing: version.revisionStatus === 'processing',
      isCompleted: version.revisionStatus === 'completed' || !version.revisionStatus
    }))
    .sort((a, b) => {
      // Sort by creation time, newest first
      const timeA = new Date(a.time_created || 0);
      const timeB = new Date(b.time_created || 0);
      return timeB - timeA;
    });
};

/**
 * Build the data structure expected by the enterprise admin UI
 * This mirrors the structure shown in the HTML file
 * @param {Object} completeData - Data from getEnterpriseUserCompleteData
 * @returns {Object} - Formatted data structure for the UI
 */
export const formatDataForEnterpriseUI = (completeData) => {
  if (!completeData || !completeData.data) {
    return null;
  }
  
  const { data } = completeData;
  
  return {
    enterpriseName: data.enterpriseName,
    userEmail: data.userEmail,
    userId: data.userId,
    projects: data.projects.map(project => ({
      projectId: project.projectId,
      projectTitle: project.projectTitle,
      createdAt: project.createdAt,
      views: project.views.map(view => ({
        viewId: view.viewId,
        viewTitle: view.viewTitle,
        createdAt: view.createdAt,
        versions: formatVersionsForDisplay(view.versions),
        latestVersion: view.versions && view.versions.length > 0 ? view.versions[0] : null
      }))
    }))
  };
};